import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getClientById, getClients } from '@services/clients.service';
import { useLocation, useNavigate } from 'react-router-dom';

export const CLIENTS_MODULE_QUERY_KEY = (params) => ['clients', 'directory', params];
export const CLIENTS_DETAIL_QUERY_KEY = (clientId) => ['clients', 'directory', 'detail', String(clientId ?? '')];

export const CLIENTS_VIEW = {
    ALL: 'all',
    INHOUSE: 'inhouse',
};

function toTitleWords(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '-';
    return s
        .replace(/_/g, ' ')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

function formatClientType(type) {
    const t = String(type ?? '').trim().toLowerCase();
    if (t === 'in_house') return 'In House';
    if (t === 'managed') return 'Managed';
    return toTitleWords(t);
}

export function useClientsModule(view) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);

    const query = useQuery({
        queryKey: CLIENTS_MODULE_QUERY_KEY({ view, searchText, page, limit, sortBy, sortOrder }),
        queryFn: () =>
            getClients({
                page,
                limit,
                sortBy,
                sortOrder,
                q: searchText || undefined,
                type: view === CLIENTS_VIEW.INHOUSE ? 'in_house' : 'all',
            }),
    });

    const clients = Array.isArray(query.data?.data) ? query.data.data : [];
    const total = Number(query.data?.total ?? 0) || 0;

    const rows = useMemo(() => {
        return clients.map((c) => ({
            key: c?.id,
            id: c?.id,
            name: c?.name ?? '-',
            email: c?.email ?? '-',
            phone: c?.phone ?? '-',
            type: c?.type ?? '-',
            typeLabel: formatClientType(c?.type),
        }));
    }, [clients]);

    const detailQuery = useQuery({
        queryKey: CLIENTS_DETAIL_QUERY_KEY(selectedClientId),
        queryFn: () => getClientById(selectedClientId),
        enabled: detailModalOpen && selectedClientId != null && selectedClientId !== '',
        staleTime: 30_000,
    });

    const selectedClientFromList = useMemo(() => {
        if (selectedClientId == null) return null;
        return rows.find((r) => String(r?.id) === String(selectedClientId)) ?? null;
    }, [rows, selectedClientId]);

    const selectedClient = useMemo(() => {
        const detailed = detailQuery.data ?? null;
        if (!detailed) return selectedClientFromList;
        // Merge list-friendly labels into detailed response without assuming backend shape.
        const typeLabel = formatClientType(detailed?.type ?? selectedClientFromList?.type);
        return {
            ...selectedClientFromList,
            ...detailed,
            typeLabel,
        };
    }, [detailQuery.data, selectedClientFromList]);

    const columns = useMemo(
        () => [
            { title: 'Name', dataIndex: 'name' },
            { title: 'Email', dataIndex: 'email' },
            { title: 'Phone', dataIndex: 'phone' },
            {
                title: 'Type',
                dataIndex: 'typeLabel',
                render: (v) =>
                    React.createElement(
                        'span',
                        { style: { fontWeight: 600 } },
                        v,
                    ),
            },
        ],
        [],
    );

    const openClient = (clientId) => {
        if (!clientId) return;
        setSelectedClientId(clientId);
        setDetailModalOpen(true);
    };

    const openProject = (projectId) => {
        if (!projectId) return;
        const isTenantPath = String(location?.pathname || '').startsWith('/tenant/');
        const base = isTenantPath ? '/tenant/projects' : '/projects';
        navigate(`${base}/${encodeURIComponent(String(projectId))}`);
    };

    return {
        view,
        searchText,
        setSearchText,
        page,
        limit,
        total,
        sortBy,
        sortOrder,
        setPage,
        setLimit,
        setSortBy,
        setSortOrder,
        columns,
        rows,
        openClient,
        openProject,
        selectedClient,
        detailModalOpen,
        closeDetailModal: () => setDetailModalOpen(false),
        detailLoading: detailQuery.isPending,
        detailError: detailQuery.isError,
        detailErrorObject: detailQuery.error,
        isLoading: query.isPending,
        isError: query.isError,
        error: query.error,
    };
}

